<template>
  <div class="em-toggle-pill-group" :class="[`gap-${gap}`]">
    <div
      v-for="option in options"
      :key="option.value"
      class="toggle-pill"
      :class="{ active: isActive(option.value), disabled: props.disabled }"
    >
      <EMToggleSwitch
        :model-value="isActive(option.value)"
        :label="option.label"
        label-position="left"
        :disabled="props.disabled || option.disabled"
        @update:model-value="toggleOption(option.value)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import EMToggleSwitch from './EMToggleSwitch.vue';

export interface TogglePillOption {
  label: string;
  value: string;
  disabled?: boolean;
}

interface Props {
  modelValue: string[] | string | null;
  options: TogglePillOption[];
  gap?: 'small' | 'medium' | 'large';
  multiple?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  gap: 'medium',
  multiple: true,
});

const emit = defineEmits<{
  'update:modelValue': [value: string[] | string | null];
  change: [value: string[] | string | null];
}>();

const isActive = (value: string): boolean => {
  if (props.multiple) {
    return Array.isArray(props.modelValue) && props.modelValue.includes(value);
  }
  return props.modelValue === value;
};

const toggleOption = (value: string) => {
  if (props.multiple) {
    const current = Array.isArray(props.modelValue) ? props.modelValue : [];
    const newValue = isActive(value) ? current.filter((v) => v !== value) : [...current, value];
    emit('update:modelValue', newValue);
    emit('change', newValue);
  } else {
    const newValue = isActive(value) ? null : value;
    emit('update:modelValue', newValue);
    emit('change', newValue);
  }
};
</script>

<style scoped>
.em-toggle-pill-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.em-toggle-pill-group.gap-small {
  gap: 8px;
}

.em-toggle-pill-group.gap-medium {
  gap: 12px;
}

.em-toggle-pill-group.gap-large {
  gap: 16px;
}

.toggle-pill {
  display: inline-flex;
  align-items: center;
}
</style>
